import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';

import dateToString from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { ChevronBottom } from '@yandex-360/iconostasis/dist/icons/ChevronBottom';
import { ChevronOffsetLeft } from '@yandex-360/iconostasis/dist/icons/ChevronOffsetLeft';
import { ChevronTop } from '@yandex-360/iconostasis/dist/icons/ChevronTop';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { Divider } from '@yandex-360/orb-components/dist/components/Divider';
import { ListBoxItemProps } from '@yandex-360/orb-components/dist/components/ListBoxItem';
import { Popover } from '@yandex-360/orb-components/dist/components/Popover2';
import { Select } from '@yandex-360/orb-components/dist/components/Select2';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { useSelector } from 'react-redux';

import { clearNotifications } from '../../../../../../shared/features/notifications/ps-notifications';
import { i18n, i18nMonthsNominative } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { ARTIFACTS_FILTER_OPTION, DATE_RANGE_TYPE } from '../../../../constants';
import { useArtifactsOptions } from '../../../../helpers/get-artifacts-options';
import { getRangeTypeLabelMap } from '../../../../helpers/get-range-type-label-map';
import { selectDateRangeType, selectFilterArtifacts, selectFilterSelectedMonth } from '../../../../selectors';
import { FilterDatePicker } from '../../../filter/components/filter-date-picker';
import { FilterPeriodPicker } from '../../../filter/components/filter-period-picker';
import { MonthsListBox } from '../../../filter/components/months-list-box';
import { RangeTypeListBox } from '../../../filter/components/range-type-list-box';
import {
    resetAllFilters,
    setFilterArtifacts,
    setFilterDateRangeType,
    setFilterFromTo,
    setFilterSelectedMonth,
} from '../../../filter/slice';
import styles from './styles.css';

type TwoColHeaderProps = {
    onBackButtonClick: () => void;
};

export const TwoColHeader: FunctionComponent<TwoColHeaderProps> = ({ onBackButtonClick }) => {
    const dateRangeTypeLabelMap = useMemo(getRangeTypeLabelMap, []);

    const dateFilterRef = useRef(null);
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
    const dateRangeType = useSelector(selectDateRangeType);

    const specificMonthRef = useRef(null);
    const [isMonthFilterOpen, setIsMonthFilterOpen] = useState(false);
    const selectedMonth = useSelector(selectFilterSelectedMonth);

    const actions = useActions({
        setFilterDateRangeType,
        setFilterArtifacts,
        setFilterFromTo,
        setFilterSelectedMonth,
        clearNotifications,
        resetAllFilters,
    });

    const onDateRangeTypeSelected = useCallback(
        (item: ListBoxItemProps<DATE_RANGE_TYPE>) => {
            actions.setFilterDateRangeType(item.itemKey);
            setIsDateFilterOpen(false);
            setIsMonthFilterOpen(false);
            if (item.itemKey === DATE_RANGE_TYPE.FOR_3_MONTH) {
                actions.setFilterFromTo({ from: null, to: null });
            }
        },
        [actions]
    );

    const onMonthSelected = useCallback(
        ({ itemKey }: ListBoxItemProps<Date>) => {
            actions.setFilterDateRangeType(DATE_RANGE_TYPE.SPECIFIC_MONTH);
            actions.setFilterSelectedMonth(itemKey);
            setIsDateFilterOpen(false);
            setIsMonthFilterOpen(false);
            const to = new Date(itemKey);
            to.setMonth(to.getMonth() + 1);
            to.setDate(to.getDate() - 1);
            actions.setFilterFromTo({ from: dateToString(itemKey, 'YYYY-MM-DD'), to: dateToString(to, 'YYYY-MM-DD') });
        },
        [actions]
    );

    const artifactsSelectOptions = useArtifactsOptions();
    const selectedArtifacts = useSelector(selectFilterArtifacts);
    const onResetPress = useCallback(() => {
        countMetrika('meeting_history', 'reset_filters');
        actions.resetAllFilters();
    }, [actions]);
    const onArtifactsChange = useCallback(
        (artifacts: ARTIFACTS_FILTER_OPTION) => {
            actions.setFilterArtifacts(artifacts);
        },
        [actions]
    );
    return (
        <>
            <div className={styles.conferencesHistoryHeader}>
                <div className={styles.top}>
                    <div className={styles.left}>
                        <Button
                            onPress={() => {
                                onBackButtonClick();
                            }}
                            view="neutral-transparent"
                            size="sm"
                            icon={<ChevronOffsetLeft variant="outline" size="sm" />}
                        >
                            {i18n('conferences-history', 'home')}
                        </Button>
                        <Text as="h1" className={styles.title} typography="heading-sm">
                            {i18n('conferences-history', 'conferences-history')}
                        </Text>
                    </div>
                    <div className={styles.right}></div>
                </div>
                <div className={styles.filter}>
                    <div className={styles.dateFilter}>
                        <div ref={dateFilterRef}>
                            <Button
                                iconRight={
                                    isDateFilterOpen ? (
                                        <ChevronTop variant="outline" size="sm" />
                                    ) : (
                                        <ChevronBottom variant="outline" size="sm" />
                                    )
                                }
                                onPress={() => {
                                    setIsDateFilterOpen(!isDateFilterOpen);
                                    if (isMonthFilterOpen) {
                                        setIsMonthFilterOpen(false);
                                    }
                                }}
                            >
                                {dateRangeType === DATE_RANGE_TYPE.SPECIFIC_MONTH
                                    ? i18nMonthsNominative()[selectedMonth?.getMonth() as number]
                                    : dateRangeTypeLabelMap[dateRangeType]}
                            </Button>
                        </div>
                        <Popover
                            className={styles.popover}
                            triggerRef={dateFilterRef}
                            isOpen={isDateFilterOpen}
                            onOpenChange={setIsDateFilterOpen}
                            placement="bottom left"
                            preventInteractOutside={false}
                        >
                            <RangeTypeListBox
                                onDateRangeTypeSelected={onDateRangeTypeSelected}
                                toggleIsMonthFilterOpen={() => setIsMonthFilterOpen(!isMonthFilterOpen)}
                                specificMonthRef={specificMonthRef}
                            />
                        </Popover>
                        <Popover
                            isOpen={isMonthFilterOpen}
                            placement="right top"
                            triggerRef={specificMonthRef}
                            preventInteractOutside={false}
                            onOpenChange={() => {
                                setIsDateFilterOpen(false);
                                setIsMonthFilterOpen(false);
                            }}
                            excludeRefs={[dateFilterRef, specificMonthRef]}
                        >
                            <MonthsListBox onMonthSelected={onMonthSelected} />
                        </Popover>
                        {dateRangeType === DATE_RANGE_TYPE.SPECIFIC_DATES && <FilterPeriodPicker />}
                        {dateRangeType === DATE_RANGE_TYPE.SPECIFIC_DATE && <FilterDatePicker />}
                    </div>
                    <Divider orientation="vertical" className={styles.divider} />
                    <Select
                        items={artifactsSelectOptions}
                        selectedKey={selectedArtifacts}
                        onSelectionChange={onArtifactsChange}
                    />
                    {(dateRangeType !== DATE_RANGE_TYPE.FOR_3_MONTH ||
                        selectedArtifacts !== ARTIFACTS_FILTER_OPTION.ALL_CONFS) && (
                        <>
                            <Divider orientation="vertical" className={styles.divider} />
                            <Button view="neutral-transparent" onPress={onResetPress}>
                                {i18n('conferences-history', 'reset')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
